import {
  Stack,
  Paper,
  Box,
  Group,
  Slider,
  Select,
  Text,
  ScrollArea,
} from '@mantine/core';
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import clsx from 'clsx';
import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { LineAreaSatChart } from '../../components/line-area-sat-chart/line-area-sat-chart';
import { intercultureQueries } from '../../data-access/queries/interculture';

import classes from './page.module.css';

import {
  formatDate,
  layersAdminOptions,
  useQueriesWithGlobal,
} from '@data-access';
import {
  asFieldsFeatureCollectionFromFeatures,
  hasFieldGeometry,
  useFieldsQueries,
} from '@fields/data-access';
import {
  PfMapProps,
  zoomOnFeature,
  PfMap,
  PlotsLayer,
  SelectImageSlider,
  SwitchControl,
  LegendControl,
  GradientLegend,
  useWmsLayerInfo,
  WmsLayer,
} from '@map';

export function IntercultureSatImagesPage() {
  const { t } = useTranslation();
  const { formId, crop } = useParams({
    from: '/_app/interculture_/$crop/$formId/result/sat-images',
  });
  const viewport = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  const [heatmapType, setHeatmapType] = useState('RGB');
  const [rasterOpacity, setRasterOpacity] = useState(100);
  const [rasterOpacityEnd, setRasterOpacityEnd] = useState(100);
  const [showGraph, setShowGraph] = useState(false);

  const queries = useQueriesWithGlobal(intercultureQueries);
  const fieldsQueries = useFieldsQueries();

  const intercultureForm = useSuspenseQuery(
    queries.result({
      id: Number(formId),
      crop,
    }),
  );
  const field = useSuspenseQuery(
    fieldsQueries.detail(intercultureForm.data.field.id),
  );
  const { data } = useSuspenseQuery(
    queries.debugPanelData({
      fieldId: field.data.id,
      yieldId: intercultureForm.data.yield_data!.id,
      layer: heatmapType,
    }),
  );

  const initialSelected = useMemo(
    () =>
      data?.data
        .filter((heatmap) => !heatmap.clouds)
        .map((heatmap) => heatmap.id.toString()) || [],
    [data?.data],
  );
  const [selectedHeatmap, setSelectedHeatmap] = useState<string | null>(
    initialSelected[0] || null,
  );

  const heatmapData = data?.data.find(
    (heatmap) => heatmap.id.toString() === selectedHeatmap,
  );

  const { handleWmsLayerClick, wmsInfoPopup } = useWmsLayerInfo({
    imageryIndex: heatmapType,
    wmsSrc: heatmapData?.url,
  });

  const handleMapLoad: PfMapProps['onLoad'] = (map) => {
    zoomOnFeature({
      geojson: field.data,
      map: map.target,
    });
  };

  const handleSelectImageChange = (value: string) => {
    setSelectedHeatmap(value);
    scrollGraphTo(value);
  };
  const scrollGraphTo = useCallback(
    (id: string) => {
      const index = data.graphData.findIndex((d) => d.id === id);
      if (index !== -1) {
        viewport.current?.scrollTo({
          left: index * 150,
          behavior: 'smooth',
        });
      }
    },
    [data.graphData],
  );

  useEffect(() => {
    if (data.graphData.length && showGraph) {
      scrollGraphTo(data.graphData[data.graphData.length - 1].id);
    }
  }, [data.graphData, scrollGraphTo, showGraph]);

  useEffect(() => {
    layersAdminOptions(t).forEach((option) => {
      queryClient.prefetchQuery(
        queries.debugPanelData({
          fieldId: field.data.id,
          yieldId: intercultureForm.data.yield_data!.id,
          layer: option.value,
        }),
      );
    });
  }, [field.data.id, intercultureForm.data.yield_data?.id]);

  return (
    <Stack h="100%" style={{ overflow: 'hidden' }}>
      <Paper>
        <Group justify="space-between">
          <div />
          <Group>
            <Box miw={250}>
              <Text size="md">Opacité de la carte</Text>
              <Slider
                marks={[{ value: 20 }, { value: 50 }, { value: 80 }]}
                onChange={setRasterOpacity}
                onChangeEnd={setRasterOpacityEnd}
                value={rasterOpacity}
              />
            </Box>
            <Select
              data={layersAdminOptions(t)}
              onChange={(v) => setHeatmapType(v ?? 'LAI')}
              value={heatmapType}
            />
          </Group>
        </Group>
      </Paper>
      <Paper h="100%" p={0}>
        <Stack gap={0} h="100%">
          <PfMap
            classnames={{
              container: clsx({
                [classes.map]: heatmapType === 'NDVI' || heatmapType === 'LAI',
              }),
            }}
            onClick={handleWmsLayerClick}
            onLoad={handleMapLoad}
          >
            {(heatmapType === 'LAI' || heatmapType === 'NDVI') &&
            data.legend ? (
              <LegendControl>
                <GradientLegend legend={data.legend} />
              </LegendControl>
            ) : null}
            <SwitchControl
              checked={showGraph}
              disabled={data.graphData.length === 0}
              label={t('common.show_graph')}
              onChange={setShowGraph}
            />
            {wmsInfoPopup}
            {heatmapData ? (
              <WmsLayer
                maxbounds={data.maxbounds}
                opacity={rasterOpacityEnd / 100}
                url={heatmapData.url}
              />
            ) : null}
            {hasFieldGeometry(field.data) ? (
              <PlotsLayer
                data={asFieldsFeatureCollectionFromFeatures([field.data])}
                outline
              />
            ) : null}
          </PfMap>
          <SelectImageSlider
            key={heatmapType + data?.data?.length}
            onChange={handleSelectImageChange}
            options={
              data?.data.map((heatmap) => ({
                value: heatmap.id.toString(),
                src: heatmap.thumbnail,
                valueLabel: formatDate({
                  date: heatmap.date,
                  format: 'display',
                }),
                manual_check: heatmap.manual_check,
              })) || []
            }
            value={selectedHeatmap || ''}
          />
          {showGraph && data.graphData.length ? (
            <ScrollArea
              flex="0 0 auto"
              h={250}
              scrollbars={'x'}
              viewportRef={viewport}
            >
              <LineAreaSatChart
                data={data.graphData}
                domain={data.graph.domain}
                harvestDate={data.harvestDate}
                // previousHarvestDate={formatDate({
                //   date: intercultureForm.data.previous_crop_harvest_date,
                //   format: 'display',
                // })}
                previousHarvestDate={data.previousHarvestDate}
                selectedHeatmap={selectedHeatmap}
                setSelectedHeatmap={setSelectedHeatmap}
                sowingDate={data.sowingDate}
              />
            </ScrollArea>
          ) : null}
        </Stack>
      </Paper>
    </Stack>
  );
}

export default IntercultureSatImagesPage;
